/**
 * ArkUI.Sortable - 基于 Sortable.js 的拖拽排序封装
 *
 * 依赖：sortablejs (npm install sortablejs)
 * 版本：1.15.6
 *
 * 关键修复：
 * - 禁用 fallback 元素的 transition，避免位置更新延迟
 * - 使用 fallbackOnBody 解决 Dialog overflow 裁剪问题
 * - 使用最高 z-index 确保在 Dialog 之上
 */

import Sortable from 'sortablejs';

const instances = new Map();
let stylesInjected = false;

/**
 * 注入拖拽样式
 */
function injectStyles() {
    if (stylesInjected) return;

    const style = document.createElement('style');
    style.id = 'ark-sortable-styles';
    style.textContent = `
        /* 占位符（原位置的虚影） */
        .ark-sortable-ghost,
        .ark-seq-ghost {
            opacity: 0.4 !important;
            background: #dbeafe !important;
            border: 2px dashed #3b82f6 !important;
            transition: none !important;
        }
        
        /* 被选中的元素 */
        .ark-sortable-chosen,
        .ark-seq-chosen {
            opacity: 0.6 !important;
            transition: none !important;
        }
        
        /* 原位置元素（隐藏） */
        .ark-sortable-drag,
        .ark-seq-drag {
            opacity: 0 !important;
            transition: none !important;
        }
        
        /* ★★★ fallback 克隆元素（跟随鼠标） ★★★ */
        .sortable-fallback {
            position: fixed !important;
            z-index: 2147483647 !important;
            opacity: 0.95 !important;
            background: white !important;
            box-shadow: 0 25px 80px rgba(0, 0, 0, 0.35),
                        0 10px 30px rgba(0, 0, 0, 0.2) !important;
            border: 2px solid #3b82f6 !important;
            border-radius: 10px !important;
            pointer-events: none !important;
            cursor: grabbing !important;
            /* ★ 关键：禁用 transition，否则位置更新会有延迟 */
            transition: none !important;
            -webkit-transition: none !important;
            animation: none !important;
        }
        
        /* 拖拽时禁用页面选择 */
        body.ark-sortable-dragging {
            user-select: none !important;
            -webkit-user-select: none !important;
            cursor: grabbing !important;
        }
        
        body.ark-sortable-dragging * {
            cursor: grabbing !important;
        }
        
        /* 暗色模式 */
        @media (prefers-color-scheme: dark) {
            .ark-sortable-ghost,
            .ark-seq-ghost {
                background: #1e3a5f !important;
                border-color: #60a5fa !important;
            }
            
            .sortable-fallback {
                background: #1f2937 !important;
                border-color: #60a5fa !important;
                color: white !important;
            }
        }
        
        .dark .ark-sortable-ghost,
        .dark .ark-seq-ghost {
            background: #1e3a5f !important;
            border-color: #60a5fa !important;
        }
        
        .dark .sortable-fallback {
            background: #1f2937 !important;
            border-color: #60a5fa !important;
            color: white !important;
        }
    `;
    document.head.appendChild(style);
    stylesInjected = true;
}

/**
 * 初始化排序列表
 */
export function init(containerId, dotNetRef, options = {}) {
    injectStyles();

    const container = document.getElementById(containerId);
    if (!container) {
        console.warn('[ArkSortable] Container not found:', containerId);
        return false;
    }

    destroy(containerId);

    const {
        handle = null,
        animation = 150,
        ghostClass = 'ark-sortable-ghost',
        chosenClass = 'ark-sortable-chosen',
        dragClass = 'ark-sortable-drag',
        delay = 0,
        delayOnTouchOnly = true,
        group = null,
        revertOnAdd = false,
    } = options;

    const sortable = new Sortable(container, {
        handle,
        animation,
        ghostClass,
        chosenClass,
        dragClass,
        fallbackClass: 'sortable-fallback',
        // 跨容器拖拽：同 group 名的容器之间可互拖（看板视图场景）
        ...(group ? { group: { name: group, pull: true, put: true } } : {}),

        // ★★★ 核心配置 ★★★
        forceFallback: true,    // 强制使用 fallback 模式
        fallbackOnBody: true,   // 克隆到 body，解决 overflow 裁剪
        fallbackTolerance: 0,

        delay,
        delayOnTouchOnly,
        filter: '.ark-sortable-disabled',
        preventOnFilter: true,

        onStart: (evt) => {
            document.body.classList.add('ark-sortable-dragging');

            dotNetRef?.invokeMethodAsync('OnDragStart', {
                oldIndex: evt.oldIndex,
                itemId: evt.item.dataset.id
            }).catch(() => {});
        },

        onEnd: (evt) => {
            document.body.classList.remove('ark-sortable-dragging');

            // 跨容器拖拽:数据迁移已由 onAdd/onRemove 完整接管,onEnd 不再发冗余 OnDragEnd 事件
            if (evt.from !== evt.to) {
                return;
            }

            if (evt.oldIndex === evt.newIndex && evt.from === evt.to) {
                return;
            }

            // 只收**直接子节点**：querySelectorAll 会穿透整棵子树，把嵌套列表的
            // data-id 一并捞进来（打印模板「明细行」里就嵌着「明细列」那一层）。
            // 两层的 id 由各自的 SortableHelper 从 item-1 起独立编号、必然撞号，
            // C# 侧按 id 反查会把内层的列错认成外层的行，落点后整组顺序全乱。
            // SortableJS 的可拖项本来就只认直接子节点，这里与之对齐。
            const items = Array.from(container.children).filter(el => el.hasAttribute('data-id'));
            const newOrder = items.map((el, index) => ({
                id: el.getAttribute('data-id'),
                index: index
            }));

            dotNetRef?.invokeMethodAsync('OnDragEnd', {
                oldIndex: evt.oldIndex,
                newIndex: evt.newIndex,
                itemId: evt.item.dataset.id,
                newOrder: newOrder
            }).catch(() => {});
        },

        onMove: (evt) => {
            if (evt.related.classList.contains('ark-sortable-disabled')) {
                return false;
            }
            return true;
        },

        onAdd: (evt) => {
            // 防双 DOM 所有权：把元素 revert 回源容器原位置，让 Blazor 数据驱动后续 DOM
            if (revertOnAdd) {
                const refNode = evt.from.children[evt.oldIndex] || null;
                evt.from.insertBefore(evt.item, refNode);
            }

            dotNetRef?.invokeMethodAsync('OnItemAdded', {
                itemId: evt.item.dataset.id,
                fromContainerId: evt.from.id,
                toContainerId: evt.to.id,
                newIndex: evt.newIndex
            }).catch(() => {});
        },

        onRemove: (evt) => {
            dotNetRef?.invokeMethodAsync('OnItemRemoved', {
                itemId: evt.item.dataset.id,
                fromContainerId: evt.from.id,
                toContainerId: evt.to.id,
                oldIndex: evt.oldIndex
            }).catch(() => {});
        }
    });

    instances.set(containerId, { sortable, dotNetRef });
    return true;
}

/**
 * 销毁排序实例
 */
export function destroy(containerId) {
    const instance = instances.get(containerId);
    if (instance) {
        instance.sortable.destroy();
        instances.delete(containerId);
    }
}

/**
 * 启用/禁用排序
 */
export function setDisabled(containerId, disabled) {
    const instance = instances.get(containerId);
    if (instance) {
        instance.sortable.option('disabled', disabled);
    }
}

/**
 * 获取当前顺序
 */
export function getOrder(containerId) {
    const instance = instances.get(containerId);
    return instance ? instance.sortable.toArray() : [];
}

/**
 * 设置顺序
 */
export function setOrder(containerId, order) {
    const instance = instances.get(containerId);
    if (instance) {
        instance.sortable.sort(order, true);
    }
}

/**
 * 刷新列表
 */
export function refresh(containerId) {
    // Sortable 自动检测 DOM 变化
}

// 全局挂载
if (typeof window !== 'undefined') {
    window.ArkUI = window.ArkUI || {};
    window.ArkUI.Sortable = {
        init,
        destroy,
        setDisabled,
        getOrder,
        setOrder,
        refresh
    };
}

export default { init, destroy, setDisabled, getOrder, setOrder, refresh };